/**
 * §7.1 information architecture. Navigation is organised by relationship to a
 * bot, not by role: Owner and Renter are per-bot relationships and one user is
 * routinely both at once, so there is no split into two role dashboards here and
 * no place to add one.
 *
 * Staff roles are deliberately absent. Moderation and admin are a separate app
 * on a separate subdomain; a staff link in this nav would be the first step to
 * merging the two surfaces.
 *
 * Shell shape: at >=768px the rail is the only chrome — brand at the top,
 * navigation in the middle, identity/auth pinned to the bottom — and the main
 * column scrolls on its own with the viewport locked to 100dvh. One chrome
 * region instead of a header plus a sidebar both asking for attention; the
 * page's own heading is the title, so a second bar above it was duplication.
 *
 * §7.8: >=1280 full rail, 768-1279 a narrow rail, <768 a top bar + drawer.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSession } from '../session/SessionContext';
import { useLayout } from '../hooks/useMediaQuery';
import { NotificationBell, NotificationPanel } from './NotificationCentre';

interface NavItem {
  to: string;
  label: string;
  short: string;
}

const GUEST_NAV: NavItem[] = [{ to: '/browse', label: 'Browse', short: 'Browse' }];

const AUTH_NAV: NavItem[] = [
  { to: '/browse', label: 'Browse', short: 'Browse' },
  { to: '/bots', label: 'My Bots', short: 'Bots' },
  { to: '/rentals', label: 'My Rentals', short: 'Rentals' },
  { to: '/account/profile', label: 'Account', short: 'Account' },
];

const ACCOUNT_NAV: NavItem[] = [
  { to: '/account/billing', label: 'Billing', short: 'Billing' },
  { to: '/account/credits', label: 'Credits', short: 'Credits' },
  { to: '/account/profile', label: 'Profile', short: 'Profile' },
  { to: '/account/security', label: 'Security', short: 'Security' },
  { to: '/account/notifications', label: 'Notifications', short: 'Alerts' },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { me, authenticated } = useSession();
  const layout = useLayout();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  // A route change must not leave an overlay covering the destination.
  useEffect(() => {
    setDrawerOpen(false);
    setPanelOpen(false);
  }, [location.pathname]);

  const items = authenticated ? AUTH_NAV : GUEST_NAV;
  const inAccount = location.pathname.startsWith('/account');

  const nav = (
    <nav className="app-nav" aria-label="Primary">
      <ul>
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                `nav-link${isActive || (item.to.startsWith('/account') && inAccount) ? ' nav-link-active' : ''}`
              }
            >
              {layout === 'medium' ? item.short : item.label}
            </NavLink>
            {item.to.startsWith('/account') && inAccount ? (
              <ul className="nav-sub">
                {ACCOUNT_NAV.map((sub) => (
                  <li key={sub.to}>
                    <NavLink
                      to={sub.to}
                      className={({ isActive }) => `nav-sublink${isActive ? ' nav-link-active' : ''}`}
                    >
                      {layout === 'medium' ? sub.short : sub.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </nav>
  );

  /**
   * Identity and auth live at the rail's foot: the last thing in the reading
   * order of the chrome, present on every screen, never competing with the
   * page heading for the top edge.
   */
  const railFoot = authenticated ? (
    <div className="rail-foot">
      <NotificationBell onOpen={() => setPanelOpen((v) => !v)} />
      <Link to="/account/profile" className="who">
        @{me?.handle}
      </Link>
    </div>
  ) : (
    <div className="rail-foot rail-foot-guest">
      <Link to="/signup" className="btn btn-primary">
        Create an account
      </Link>
      <Link to="/login" className="btn btn-ghost">
        Sign in
      </Link>
    </div>
  );

  if (layout === 'compact') {
    // Phones keep a top bar: a bottom-anchored rail would fight the keyboard
    // and every mobile convention users already have.
    return (
      <div className="app app-compact">
        <a className="skip-link" href="#main">
          Skip to content
        </a>

        <header className="app-header">
          <div className="app-header-left">
            <button
              type="button"
              className="btn btn-ghost"
              aria-expanded={drawerOpen}
              aria-controls="nav-drawer"
              onClick={() => setDrawerOpen((v) => !v)}
            >
              Menu
            </button>
            <Link to="/browse" className="brand">
              Persona Marketplace
            </Link>
          </div>

          <div className="app-header-right">
            {authenticated ? (
              <>
                <NotificationBell onOpen={() => setPanelOpen((v) => !v)} />
                <Link to="/account/profile" className="who">
                  @{me?.handle}
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">
                  Sign in
                </Link>
                <Link to="/signup" className="btn btn-primary btn-sm" aria-label="Create an account">
                  Sign up
                </Link>
              </>
            )}
          </div>
        </header>

        {panelOpen ? <NotificationPanel onClose={() => setPanelOpen(false)} /> : null}
        {drawerOpen ? (
          <div className="nav-drawer" id="nav-drawer">
            {nav}
          </div>
        ) : null}

        <main className="app-main" id="main">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className={`app app-${layout}`}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <aside className={layout === 'medium' ? 'rail rail-narrow' : 'rail'}>
        <Link to="/browse" className="brand rail-brand">
          {layout === 'medium' ? 'PM' : 'Persona Marketplace'}
        </Link>
        {nav}
        {railFoot}
      </aside>

      {panelOpen ? <NotificationPanel onClose={() => setPanelOpen(false)} /> : null}

      <main className="app-main" id="main">
        {children}
      </main>
    </div>
  );
}
